import React, { useEffect, useMemo } from "react";
import { Direction, IRoute, ITravel, getApiCallState, useStateSelector } from "../Models";
import { BookingFooter } from "../Components/BookingFooter";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { useDispatch } from "react-redux";
import { loadServiceInfo, selectRoute, setStep } from "../Actions";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { Spinner, SpinnerSmall } from "../../Shared/Components/Spinner";
import SvgArrowsWhite from "../../Shared/Icons/ArrowsWhite";
import { StopSelector } from "../Components/StopSelector";
import { setOrigin, setDestination, searchJourney, setDepartureDate, setReturnDate } from "../Search/Actions";
import SvgFastclock from "../../Shared/Icons/Fastclock";
import dayjs from "dayjs";
import { formatDuration } from "../../Shared/Utils";
import SvgChevronRight from "../../Shared/Icons/bootstrap/ChevronRight";
import SvgChevronLeft from "../../Shared/Icons/bootstrap/ChevronLeft";
import SvgAlertTriangle from "../../Shared/Icons/bootstrap/AlertTriangle";
import { clearApiCallState } from "../ApiCalls/Actions";
import { getTravelMethodIcon, getTravelMethodText } from "../Settings";
import { isValidSearch } from "../Search/Search";
import { getErrorMessages } from "../Shared/Utils";
import { Price } from "../Components/Price";

const strings = getStrings().component.selectJourney;

export const SelectJourneys = () => {
	const state = useStateSelector(state => state.timetable);
	const searchState = useStateSelector(state => state.search);
	const travelDetailsState = useStateSelector(state => state.travelDetails);

	const apicalls = getApiCallState();
	const loadingState = apicalls.getLoadingState("searchJourney");
	const isLoading = loadingState === "loading";
	const errors = useMemo(() => getErrorMessages("searchJourney", apicalls), [loadingState]);

	const dispatch = useDispatch();

	const enableSubmit = travelDetailsState.outbound?.route && (!searchState.isTwoWay || travelDetailsState.inbound?.route);
	const totalPrice = useMemo(() => {
		const outboundPrice = travelDetailsState.outbound?.route?.lowestPrice;
		if (!outboundPrice)
			return null;

		const inboundPrice	= travelDetailsState.inbound?.route?.lowestPrice || 0;
		return outboundPrice + inboundPrice;
	}, [travelDetailsState.outbound, travelDetailsState.inbound]);

	const goBack = () => {
		dispatch(clearApiCallState("searchJourney"));
		dispatch(setStep("search"));
	};

	const submit = () => {
		dispatch(setStep("outbound"));
	};

	useEffect(() => {
		if (!loadingState || loadingState === "loading" || !state.searchJourneyResult || !isValidSearch(searchState))
			return;

		dispatch(searchJourney(searchState));
	}, [searchState?.origin?.code, searchState.destination?.code, searchState.departureDate, searchState.returnDate])

	return <>
		<div className="body">
			<div className="select-journey content">
				<SearchSummary isLoading={isLoading} />
				<Row>
					<Col lg={6}>
						<Timetable travels={state.searchJourneyResult?.offer?.travels} date={searchState.departureDate} direction="outbound" />
					</Col>
					{ searchState.isTwoWay && <Col lg={6}>
						<Timetable travels={state.searchJourneyResult?.offer?.travels} date={searchState.returnDate} direction="inbound" />
					</Col> }
				</Row>

				{ loadingState === "error" && <Alert variant="danger">
					{strings.loadingTimetableError}
					{ errors && <ul>{errors.map((e, idx) => <li key={idx}>{e}</li>)}</ul> }
				</Alert>}

				{/* { state?.searchJourneyResult && <pre><code>{JSON.stringify(state.searchJourneyResult, null, 2)}</code></pre>} */}
			</div>
			<BookingFooter text={strings.timetableSubmit} onSubmit={enableSubmit ? submit : null} backText={strings.timetableBack} onBack={goBack} price={totalPrice} />
		</div>
	</>;
}

export const Timetable = (props: { travels?: ITravel[], direction: Direction, date: string }) => {
	const travel = props.travels?.find(x => x.direction == props.direction);
	const heading = props.direction === "outbound" ? strings.outboundJourney : strings.returnJourney;
	const date = useMemo(() => props.date ? dayjs(props.date) : null, [props.date]);

	const apicalls = getApiCallState();
	const loadingState = apicalls.getLoadingState("searchJourney");
	const isLoading = loadingState === "loading";

	const travelDetailsState = useStateSelector(state => state.travelDetails);
	const selectedRouteId = travelDetailsState[props.direction]?.route?.id;

	const dispatch = useDispatch();

	const changeDay = (step: number) => {
		if (isLoading)
			return;

		const newDate = date.add(step, "days").format("YYYY-MM-DD");
		if (props.direction === "outbound")
			dispatch(setDepartureDate(newDate));
		else
			dispatch(setReturnDate(newDate));
	};

	const routeSelected = (route: IRoute) => {
		dispatch(selectRoute(props.direction, route));
	};

	useEffect(() => {
		if (!selectedRouteId)
			return;

		//Ladda service info för alla legs på den valda routen
		const route = travelDetailsState[props.direction].route;
		const serviceIdentifiers = route.legs.filter(x => !x.serviceInformation).map(x => x.serviceIdentifier);
		dispatch(loadServiceInfo(props.direction, route.id, serviceIdentifiers));
	}, [selectedRouteId]);

	if (loadingState === "error")
		return null;

	return <section>
		<h2>{heading}</h2>
		<DateHeader date={date} changeDay={changeDay} disabled={isLoading} />
		{ isLoading ? <Alert variant="light" className="text-center"><Spinner /></Alert> :
			travel?.routes?.length > 0 ? <ul className="list-unstyled timetable">
				{travel.routes.map(route => <Route key={route.id} route={route} selected={route.id === selectedRouteId} onRouteSelected={routeSelected} />)}
			</ul> : <Alert variant="info">{strings.noPrioritizedJourneys}</Alert>
		}
	</section>;
};

const DateHeader = (props: { date: dayjs.Dayjs, changeDay: (step: number) => void, disabled: boolean}) => (<div className="timetable-date">
		<Button variant="icon" disabled={props.disabled} className="prev" onClick={() => props.changeDay(-1)}><SvgChevronLeft /></Button>
		<span className="date">{props.date?.format("dddd D MMMM YYYY")}</span>
		<Button variant="icon" disabled={props.disabled} className="prev" onClick={() => props.changeDay(1)}><SvgChevronRight /></Button>
	</div>);

const Route = (props: { route: IRoute, selected: boolean, onRouteSelected: (route: IRoute) => void }) => {
	const route = props.route;

	if (!route.legs || route.legs.length === 0)
		return null;

	//const price = useMemo(() => route.bundles?.reduce((min, bundle) => min === -1 || bundle.price < min ? bundle.price : min , -1) || 0, [route.bundles]);
	const departure = dayjs(route.legs[0].departureStation.departureTimestamp);
	const arrival = dayjs(route.legs[route.legs.length-1].arrivalStation.arrivalTimestamp);
	const duration = dayjs.duration(route.travelDuration);
	const serviceTypes = route.legs.reduce((arr, curr) => {
		if (!arr.some(x => x == curr.serviceType.modality))
			arr.push(curr.serviceType.modality);
		return arr;
	} ,[] as string[]);

	//TODO: Detta måste vara lite mer avancerat, som i JourneyAdviceOverview
	//och ta hänsyn till tidpunkt och eventuella stop i försäljningen (stop_exceptions ?)
	const isDisabled = !route.lowestPrice || !route.bundleAvailability.availability;
	const disabledText = isDisabled ? getDisabledInfo(route) : null;

	const routeClicked = () => {
		//TODO: Kolla att vi inte laddar också
		if (isDisabled)
			return;

		props.onRouteSelected(props.route);
	};

	return <li className={`journey-advice ${props.selected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`} /*title={disabledText}*/
			onClick={routeClicked}>
		<span className="time">{departure.format("HH:mm")} » {arrival.format("HH:mm")}</span>&nbsp;&nbsp;|&nbsp;
		<span className="duration"><SvgFastclock />{formatDuration(duration)}</span><br />
		<img src="/img/Snalltaget_logo.svg" className="producer" alt="Snälltåget" />
		<div className="segments-overview">
			{ serviceTypes.map(t => <span key={t} className={`travelmethod-icon travelmethod-icon-${getTravelMethodIcon(t)}`} title={getTravelMethodText(t)}></span>)}
			{ route.legs.length === 1 ? strings.journeyAdvice.direct : `${route.legs.length - 1} ${route.legs.length === 2 ? strings.journeyAdvice.change.toLowerCase() : strings.journeyAdvice.changes.toLowerCase()}`}
		</div>
		{ route.lowestPrice > 0 && <div className="price">
			<span><Price amount={route.lowestPrice} /></span>
		</div> }
		{ isDisabled && <p className="no-price">{disabledText}</p> }
	</li>;
}

const searchStrings = getStrings().component.searchForm;
const SearchSummary = (props: { isLoading: boolean }) => {

	const dispatch = useDispatch();

	const searchState = useStateSelector(state => state.search);

	const swapStops = () => {
		dispatch(setOrigin(searchState.destination));
		dispatch(setDestination(searchState.origin));
	};

	return <Form className="search-form">
		{ searchState.stops ?
		<Row>
			<Col lg={6} className="utresa">
				<StopSelector disabled={props.isLoading} label={searchStrings.travelFrom} placeholder={searchStrings.locationFromPlaceholder} stops={searchState.stops} selected={searchState.origin} disabledStop={searchState.destination} onStopSelected={stop => dispatch(setOrigin(stop))} />
				<Button type="button" variant="link" className="swapbutton" disabled={props.isLoading} onClick={swapStops}><SvgArrowsWhite /></Button>
			</Col>

			<Col lg={6} className="hemresa">
				<StopSelector disabled={props.isLoading} label={searchStrings.travelTo} placeholder={searchStrings.locationToPlaceholder} stops={searchState.stops} selected={searchState.destination} disabledStop={searchState.origin} onStopSelected={stop => dispatch(setDestination(stop))} />
			</Col>
		</Row> : <SpinnerSmall /> }
	</Form>
};

function getDisabledInfo(bundle: IRoute) {
	const salesRestrictionsPerLeg = bundle?.legs?.map(x => ({
		id: x.id,
		from: x.departureStation.name,
		to: x.arrivalStation.name,
		salesRestrictions: x.salesRestrictions?.map(y => strings.salesRestrictions[y.reasonCode]).filter(y => !!y) as string[] || []
	 })) || [];

	 if (!salesRestrictionsPerLeg.some(x => x.salesRestrictions.length > 0))
		return <><SvgAlertTriangle /> <em>{strings.journeyAdvice.noPriceText}</em></>;

	const showLegName = bundle.legs.length > 1;

	return <ul className="list-unstyled">
		{ salesRestrictionsPerLeg.map(leg => <React.Fragment key={leg.id}>
			{leg.salesRestrictions.map(y => <li key={y}><SvgAlertTriangle /> <em>{ showLegName && <>{leg.from} - {leg.to}: </>}{y}</em></li>)}
		</React.Fragment>)}
	</ul>;
}